'use client'

import { setImpersonationTransition } from '@/stores/authSlice'
import { AnalyticsEvents, trackEvent } from '@/utils/analytics'
import { authClient, useSession } from '@/utils/auth/auth-client'
import { useAppDispatch } from '@/utils/redux-hooks'

import { useState } from 'react'

import { toast } from 'sonner'

interface StartOptions {
  targetName: string
  targetOrgId?: string
}

/**
 * Impersonation always opens the target's landing app (pinned, else first
 * readable) rather than the impersonator's current page: impersonators work
 * out of staff apps the external users they impersonate can't read, so the
 * same page in the target's org is a guaranteed /not-authorized.
 */
const IMPERSONATION_LANDING_URL = '/post-sign-in?viaPicker=1'

const ORIGINAL_ORG_KEY = 'impersonation_original_org_id'
const ORIGINAL_PATH_KEY = 'impersonation_original_path'

/**
 * Both directions of the identity swap end in a full page load, never a client
 * navigation. The layouts, their server-seeded permissions, the RTK cache and
 * the router cache all belong to the identity the tab started with, and a soft
 * navigation kept enough of them to show an impersonator their own apps
 * (prod, 2026-10-08).
 */
function loadAs(url: string) {
  window.location.assign(url)
}

export function useImpersonation() {
  const { data: session } = useSession()
  const dispatch = useAppDispatch()
  const [isStarting, setIsStarting] = useState(false)
  const [isEnding, setIsEnding] = useState(false)

  const isImpersonating = !!session?.session?.impersonatedBy

  const startImpersonation = async (userId: string, options: StartOptions) => {
    setIsStarting(true)
    dispatch(
      setImpersonationTransition({ kind: 'start', name: options.targetName })
    )
    try {
      // From the session rather than the URL: super-admin routes such as
      // /admin/roles/[id] carry no org slug, so a path-based capture lands the
      // admin in the wrong org when impersonation ends.
      const currentPath = window.location.pathname
      const originalOrgId = session?.session?.activeOrganizationId
      if (originalOrgId) {
        sessionStorage.setItem(ORIGINAL_ORG_KEY, originalOrgId)
      }
      sessionStorage.setItem(ORIGINAL_PATH_KEY, currentPath)

      // better-fetch resolves `{ error }` on a 403 rather than throwing.
      const { error } = await authClient.$fetch('/impersonation/start', {
        method: 'POST',
        body: { userId }
      })
      if (error) {
        throw new Error(error.message || 'Failed to start impersonation.')
      }
      trackEvent(AnalyticsEvents.IMPERSONATION_STARTED, {
        surface: currentPath.split('/')[1] === 'admin' ? 'admin' : 'org_page'
      })

      // Activate the org chosen in the dialog so the destination resolves
      // against the right membership.
      if (options.targetOrgId) {
        await authClient.organization.setActive({
          organizationId: options.targetOrgId
        })
      }

      loadAs(IMPERSONATION_LANDING_URL)
    } catch (err) {
      dispatch(setImpersonationTransition(null))
      throw err instanceof Error
        ? err
        : new Error('Failed to start impersonation.')
    } finally {
      setIsStarting(false)
    }
  }

  const endImpersonation = async (redirectTo?: string) => {
    setIsEnding(true)
    dispatch(setImpersonationTransition({ kind: 'end' }))
    const originalOrgId = sessionStorage.getItem(ORIGINAL_ORG_KEY)
    sessionStorage.removeItem(ORIGINAL_ORG_KEY)
    sessionStorage.removeItem(ORIGINAL_PATH_KEY)
    try {
      const { error } = await authClient.admin.stopImpersonating()
      if (error) {
        // An expired impersonation session can't be stopped: the cookie no
        // longer resolves to anyone. Signing in again is the way back.
        if (error.status === 401) return loadAs('/sign-in')
        throw new Error(error.message || 'Failed to end impersonation.')
      }
      trackEvent(AnalyticsEvents.IMPERSONATION_ENDED)

      // Restore the original active org so the destination's withAuth checks
      // run against it. If the admin lost access to it meanwhile, the
      // destination's own gates handle the missing org.
      if (originalOrgId) {
        await authClient.organization
          .setActive({ organizationId: originalOrgId })
          .catch(() => undefined)
      }

      loadAs(redirectTo ?? '/post-sign-in?viaPicker=1')
    } catch (err) {
      dispatch(setImpersonationTransition(null))
      toast.error(
        err instanceof Error ? err.message : 'Failed to end impersonation.'
      )
    } finally {
      setIsEnding(false)
    }
  }

  return {
    isImpersonating,
    isStarting,
    isEnding,
    startImpersonation,
    endImpersonation
  }
}
